import { useMemo, useState } from "react";
import SectionTitle from "./SectionTitle";
import { badgeStyle, feedCategories, feedGroups, feedItems, type GossipItem } from "@/mocks/gossip";

function FeedCard({ item }: { item: GossipItem }) {
  return (
    <article className="group relative flex w-full cursor-pointer flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-3 transition hover:border-primary-300 md:flex-row md:items-start md:p-4">
      <div className="w-full shrink-0 overflow-hidden rounded-md md:w-52">
        <div className="relative w-full h-40 md:h-32">
          <img
            src={item.cover}
            alt={`吃瓜91首发热乎瓜速递-${item.title}`}
            title={`吃瓜91第一线前排速递：${item.title}`}
            className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
          />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {item.badges.map((b) => {
            const st = badgeStyle[b];
            return (
              <span
                key={b}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${st.cls}`}
              >
                <i className={`${st.icon} text-[12px]`} />
                {b}
              </span>
            );
          })}
          <span className="text-[11px] font-semibold text-foreground-500">{item.category}</span>
          <span className="num ml-auto inline-flex items-center gap-1 text-xs font-bold text-foreground-500">
            <i className="ri-time-line" /> {item.timeLabel}
          </span>
        </div>

        <h3 className="text-[15px] font-extrabold leading-snug text-foreground-950 md:text-[17px]">
          <a href="#comment" className="transition-colors hover:text-primary-600">
            {item.title}
          </a>
        </h3>
        <p className="hidden text-sm leading-relaxed text-foreground-700 sm:block">{item.excerpt}</p>

        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-600 pt-1">
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-chat-3-fill text-primary-500" />
            <span className="num font-bold">{item.comments}</span> 前排评论
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-fire-fill text-primary-500" /> 热乎 {item.likes} 赞
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-upload-2-line" /> 被转 {item.shares}
          </span>
          <span className="ml-auto inline-flex items-center gap-1 font-semibold text-primary-600">
            前排 27 席 <i className="ri-arrow-right-s-line text-base" />
          </span>
        </div>
      </div>
    </article>
  );
}

export default function Feed() {
  const [active, setActive] = useState("all");

  const visible = useMemo(() => {
    const base = active === "all" ? feedItems : feedItems.filter((i) => i.category === active);
    return feedGroups.map((g) => ({
      ...g,
      items: base.filter((i) => i.group === g.key),
    }));
  }, [active]);

  return (
    <div id="feed" className="flex flex-col gap-5 scroll-mt-20">
      <SectionTitle
        id="feed"
        index="01"
        title="热乎瓜 · 按时间排的第一线快讯流"
        accent="FIRST LINE · TIMELINE"
        desc="刚出锅的料都在这按时间排好，首发最快与热乎度一并标清，谁先冲看手速。"
      />

      <div className="flex flex-wrap gap-2">
        {feedCategories.map((c) => {
          const isOn = active === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setActive(c.key)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-2 text-xs font-bold transition ${
                isOn
                  ? "bg-foreground-950 text-background-50"
                  : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
              }`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-7">
        {visible.map(
          (g) =>
            g.items.length > 0 && (
              <div key={g.key} className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-6 items-center gap-1.5 rounded-md bg-accent-100 px-2.5 text-xs font-bold text-accent-900">
                    {g.key === "just" && <i className="ri-broadcast-fill" />}
                    {g.label}
                  </span>
                  <span className="num text-xs font-semibold text-foreground-500">{g.items.length} 条</span>
                  <span className="h-px flex-1 bg-background-200" />
                </div>

                <div className="relative ml-2 flex flex-col gap-3 border-l-2 border-background-300 pl-5 md:pl-6">
                  {g.items.map((item) => (
                    <div key={item.id} className="relative">
                      <span className="absolute -left-[26px] top-5 h-3 w-3 rounded-full border-2 border-background-50 bg-primary-500 md:-left-[30px]" />
                      <FeedCard item={item} />
                    </div>
                  ))}
                </div>
              </div>
            )
        )}
      </div>

      <div className="flex items-center justify-center gap-3 border-t border-dashed border-background-300 pt-5">
        <span className="inline-flex items-center gap-2 text-xs text-foreground-700">
          <i className="ri-scroll-to-bottom-line text-primary-500" />
          更早的首发瓜在下面翻 · 已到底部自动按时间续
        </span>
      </div>
    </div>
  );
}